import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../../lib/api';
import api from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Search, FolderOpen, AlertCircle, User, Clock, History } from 'lucide-react';
import { toast } from 'sonner';

const SPECIALTIES = [
  'General Medicine', 'Cardiology', 'Orthopedics', 'Pediatrics',
  'Gynecology', 'ENT', 'Dermatology', 'Neurology', 'Surgery', 'Emergency'
];

const PatientSearch = () => {
  const navigate = useNavigate();
  const [searchId, setSearchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenData, setReopenData] = useState({
    specialty: '',
    assigned_doctor_id: '',
    assigned_nurse_id: '',
    patient_type: 'outdoor',
    reason: '',
  });
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);

  const handleSearch = async () => {
    if (!searchId.trim()) {
      toast.error('Please enter Patient ID or phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await patientAPI.searchById(searchId.trim());
      setSearchResult(response.data);
      
      if (response.data.patient.status === 'deleted') {
        toast.warning('This patient record has been deleted');
      } else if (response.data.has_previous_visits) {
        toast.info(`Found patient with ${response.data.closed_cases.length} previous visit(s)`);
      }
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'Patient not found' : 'Search failed');
      setSearchResult(null);
    } finally {
      setLoading(false);
    }
  };

  const openReopenDialog = async () => {
    try {
      const [docRes, nurseRes] = await Promise.all([
        api.get('/doctors'),
        api.get('/nurses'),
      ]);
      setDoctors(docRes.data);
      setNurses(nurseRes.data);
      setReopenDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load staff data');
    }
  };

  const handleReopenCase = async () => {
    try {
      await patientAPI.reopenCase(searchResult.patient.id, {
        ...reopenData,
        previous_visit_count: searchResult.closed_cases.length
      });
      toast.success('Patient case reopened successfully!');
      setReopenDialogOpen(false);
      navigate(`/patients/${searchResult.patient.id}`);
    } catch (error) {
      toast.error('Failed to reopen case');
    }
  };

  return (
    <div data-testid="patient-search-page">
      <header className="page-header glass-header">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Patient Search</h1>
          <p className="text-slate-500">Search by Patient ID, Name, or Phone to view history & reopen cases</p>
        </div>
      </header>

      <div className="page-content max-w-4xl mx-auto">
        {/* Search Box */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Enter Patient ID (PT20251223XXXX) or Phone Number"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="text-lg"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="bg-red-500 hover:bg-red-600 px-8"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResult && (
          <div className="space-y-6">
            {/* Patient Info */}
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-semibold">
                      {searchResult.patient.full_name}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      ID: {searchResult.patient.patient_id}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={searchResult.patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {searchResult.patient.status}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800">
                      {searchResult.patient.patient_type}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-slate-500">Phone:</span>
                    <span className="ml-2 font-medium">{searchResult.patient.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Email:</span>
                    <span className="ml-2 font-medium">{searchResult.patient.email || 'N/A'}</span>
                  </div>
                  {searchResult.patient.specialty && (
                    <div>
                      <span className="text-sm text-slate-500">Specialty:</span>
                      <Badge className="ml-2 bg-purple-100 text-purple-800">{searchResult.patient.specialty}</Badge>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/patients/${searchResult.patient.id}`)}
                  >
                    <User className="w-4 h-4 mr-2" />
                    View Full Details
                  </Button>
                  
                  {searchResult.has_previous_visits && searchResult.patient.status !== 'active' && (
                    <Button
                      className="bg-green-500 hover:bg-green-600"
                      onClick={openReopenDialog}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Reopen Case - New Visit
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Previous Visits */}
            {searchResult.has_previous_visits && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <History className="w-5 h-5 text-red-500" />
                    Previous Visits ({searchResult.closed_cases.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {searchResult.closed_cases.map((visit, idx) => (
                      <div key={visit.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">Visit #{searchResult.closed_cases.length - idx}</p>
                            <p className="text-sm text-slate-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatDateTime(visit.closed_at)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Closed by: {visit.closed_by_name}
                            </p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800">
                            {visit.payment_method}
                          </Badge>
                        </div>
                        {visit.discharge_summary && (
                          <p className="text-sm text-slate-600 mt-2 italic">
                            "{visit.discharge_summary.substring(0, 150)}..."
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* No Results */}
        {searchResult === null && !loading && searchId && (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No patient found with ID: {searchId}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reopen Case Dialog */}
      <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Reopen Case - {searchResult?.patient.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>New Visit:</strong> This will start a new active case for this patient.
                Previous {searchResult?.closed_cases.length} visit(s) will remain in history.
              </p>
            </div>

            <div>
              <Label>Specialty/Department *</Label>
              <Select value={reopenData.specialty} onValueChange={(v) => setReopenData({...reopenData, specialty: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  {SPECIALTIES.map(spec => (
                    <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Patient Type *</Label>
              <Select value={reopenData.patient_type} onValueChange={(v) => setReopenData({...reopenData, patient_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  <SelectItem value="outdoor">Outdoor (OPD)</SelectItem>
                  <SelectItem value="indoor">Indoor (Admission)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason for Visit</Label>
              <Textarea
                value={reopenData.reason}
                onChange={(e) => setReopenData({...reopenData, reason: e.target.value})}
                placeholder="Reason for this visit..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setReopenDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-green-500 hover:bg-green-600"
                onClick={handleReopenCase}
                disabled={!reopenData.specialty}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Reopen Case
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientSearch;
